/* global JSX */
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { IListFilterItem } from "../../../models/common/listFilterItem";
import * as messageService from "../../../models/services/messageService";
import { ISessionState } from "../../../models/session/sessionState";
import { AddButton } from "../../framework/Button/AddButton";
import { List } from "../../framework/List/List";
import { ListActionButtons } from "../../framework/List/ListActionButtons";
import { MainListContainer } from "source/components/framework/List/MainListContainer";
import { PageTitle } from "../../framework/Page/PageTitle";
import { ListSearchContainer } from "source/components/framework/List/ListSearchContainer";
import { ListSearchFilter } from "source/components/framework/List/ListSearchFilter";
import { ActionContainer } from "source/components/framework/ActionContainer";
import { IMessageListItem } from "source/models/message/messageListItem";
import { IMessageListItems } from "source/models/message/messageListItems";
import { IPageNavMenuItem } from "source/components/framework/PageNav/PageNavMenuItems";
import { SettingsButton } from "source/components/framework/Button/SettingsButton";
import { MessageContentContainer } from "./MessageList.styles";
import { MessageListHeader } from "../MessageListHeader/MessageListHeader";
import { MessageListAttachments } from "../MessageListAttachments";
import { MessageReadReceiptDialog } from "../MessageReadReceiptDialog/MessageReadReceiptDialog";
import { MessageEditDialog } from "../MessageEditDialog";
import { EditToggleButtonHeader } from "source/components/framework/Button/EditToggleButtonHeader";
import { getMessagePageGeneralFilterFromState, getMessagePageGeneralFilterChange, IMessagePageGeneralFilter, IMessagePageFilterState } from "../MessagePageFilter";
import { ReadReceiptCategory } from "ui/Scripts/source/models/enums";
import { Box } from "@mui/material";
import * as Styled from "../../framework/List/ListLine/ListLine.styles";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";

// MessageList
interface IMessageListProp extends IMessagePageFilterState {
    sessionState: ISessionState;
    settingsActions: IPageNavMenuItem[];
    savedFilter: IListFilterItem;
    companyId: string;
    editMode: boolean;
    canEditMessages: boolean;
    preloaded?: boolean;
    list?: IMessageListItems;
    listFilterIsSet: boolean;
    units: IIdTitleNode[];
    dataId?: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    titleSuffix?: JSX.Element;
    reportTabs?: React.ReactNode;
    onSetSortData: (sortColumn: string, sortOrderIsAsc: boolean) => void;
    onLoadPageData: () => void;
    onOpenFiltersDialog: () => void;
    onOpenSettingsDialog: () => void;
    onChangeEditMode: (editMode: boolean) => Promise<void>;
}

interface IMessageListSearchFilter extends IMessagePageGeneralFilter {
    pageSize: number;
    page: number;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
}

//This class is specified only for dynamic property filtering
class MessageListSearchFilter implements IMessageListSearchFilter {
    startDate: number;
    endDate: number;
    unitIds: string[];
    companyIds: string[];
    modifyUserCodes: string[];
    dynamicTimeFilterId: number;
    pageSize: number;
    page: number;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
}

interface IMessageListState extends IMessageListSearchFilter {
    // List
    hasMore: boolean;
    intialized: boolean;
    isLoading: boolean;
    waitingPreloadedData: boolean;

    items: IMessageListItem[];
    selectedId: string;
    checkedIds: string[];

    // ReadReceipts
    readReceiptMessages: IMessageListItem[];
    shownReceiptMessageIds: string[];

    // New Dialog
    showNewDialog: boolean;

    // Dialog
    showEditDialog: boolean;
    editDialogRecordId: string;
    editDialogCompanyId: string;
    editDialogHasPrevious: boolean;
    editDialogHasNext: boolean;
    editDialogForwardMessage: boolean;
}

export class MessageList extends React.Component<IMessageListProp, IMessageListState> {
    private readReceiptProcessingIds: string[] = [];
    private readReceiptProcessedIds: string[] = [];
    messageListPageSize = 10;

    getReadReceiptMessages = (items: IMessageListItem[]) => {
        const props = this.props;
        if (props.sessionState.isSuperAdmin || props.editMode) return [];
        const shownReceiptMessageIds = this.state?.shownReceiptMessageIds ?? [];
        return items ? items.filter(i => i.requiresReceipt && !i.requiresReceiptIsRead && shownReceiptMessageIds.indexOf(i.id) < 0).reverse() : [];
    };

    constructor(props: IMessageListProp) {
        super(props);
        const generalFilterValues = getMessagePageGeneralFilterFromState(props);
        this.state = {
            ...generalFilterValues,
            intialized: props.list && !!props.preloaded,
            isLoading: false,
            waitingPreloadedData: props.preloaded,
            page: props.list ? props.list.page : 1,
            hasMore: props.list ? props.list.hasMore : true,
            filter: "",
            pageSize: props.list ? props.list.pageSize : this.messageListPageSize,
            sortColumn: props.sortColumn,
            sortOrderIsAsc: props.sortOrderIsAsc,
            items: props.list ? props.list.items : [],
            selectedId: null,
            checkedIds: [],
            // ReadReceipts
            readReceiptMessages: this.getReadReceiptMessages(props.list ? props.list.items : []),
            shownReceiptMessageIds: [],
            // New Dialog
            showNewDialog: false,
            // Dialog
            showEditDialog: false,
            editDialogRecordId: "",
            editDialogCompanyId: "",
            editDialogHasPrevious: false,
            editDialogHasNext: false,
            editDialogForwardMessage: false
        };
    }

    handleItemVisibleToUser = async (id: string) => {
        if (!id) return;
        const item = this.state.items.find(i => i.id === id);
        if (!item || item.isRead) return;
        const processing = this.readReceiptProcessingIds.indexOf(id) > -1;
        if (processing) return;
        const processed = this.readReceiptProcessedIds.indexOf(id) > -1;
        if (processed) return;
        this.readReceiptProcessingIds.push(id);
        let result: ApiSuccess = null;
        try {
            result = await messageService.sendReadReceipt(id, item.companyId, ReadReceiptCategory.Automatic);
        } finally {
            this.readReceiptProcessingIds = this.readReceiptProcessingIds.filter(i => i !== id);
        }
        if (!result) return;
        this.readReceiptProcessedIds.push(id);
        //Update manually request read receipt messages queue
        const readReceiptMessages = this.state.readReceiptMessages.slice(0);
        const index = readReceiptMessages.findIndex(i => i.id === id);
        if (index > -1) {
            readReceiptMessages[index].isRead = true;
        }
        this.setState({
            readReceiptMessages
        });
    };

    searchItems = async (searchFilter: IMessageListSearchFilter, resetItems: boolean, refreshList: boolean, moveNext: boolean = false, setDialogData: boolean = true) => {
        const props = this.props;
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => messageService.getMessageListItems(props.companyId, !refreshList ? searchFilter.pageSize : (searchFilter.page * searchFilter.pageSize), !refreshList ? searchFilter.page : 1,
            props.editMode, searchFilter.filter, searchFilter.startDate, searchFilter.endDate, searchFilter.dynamicTimeFilterId, searchFilter.companyIds, searchFilter.unitIds, searchFilter.modifyUserCodes, searchFilter.sortColumn,
            searchFilter.sortOrderIsAsc), false, false, () => this.setState({ isLoading: false }));
        if (!result) return;
        const items = List.getListItems(this.state.items, this.state.selectedId, this.state.checkedIds, result.items, resetItems, refreshList);
        let selectedId = items.selectedId;
        let index = items.items.findIndex(i => i.id === selectedId);
        let selectedItem = index > -1 ? items.items[index] : null;
        if (moveNext && selectedItem && index < items.items.length - 1) {
            index = index + 1;
            selectedItem = items.items[index];
            selectedId = items.items[index].id;
        }
        this.setState({
            ...searchFilter,
            intialized: true,
            page: !refreshList ? result.page : searchFilter.page,
            hasMore: result.hasMore,
            items: items.items,
            selectedId,
            checkedIds: items.checkedIds,
            readReceiptMessages: this.getReadReceiptMessages(items.items),
            editDialogRecordId: setDialogData ? (moveNext && selectedItem ? selectedItem.id : "") : this.state.editDialogRecordId,
            editDialogCompanyId: setDialogData ? (moveNext && selectedItem ? selectedItem.companyId : "") : this.state.editDialogCompanyId,
            editDialogHasPrevious: setDialogData ? index > 0 : this.state.editDialogHasPrevious,
            editDialogHasNext: setDialogData ? (index < items.items.length - 1 || result.hasMore) : this.state.editDialogHasNext,
        });
        this.props.onSetSortData(searchFilter.sortColumn, searchFilter.sortOrderIsAsc);
        //this.setReadReceipt(detailsItem);
    };

    refreshList = async (setDialogData: boolean = true) => {
        await this.searchItems(Base.filterPropertiesByClass(this.state, MessageListSearchFilter), false, true, false, setDialogData);
    };

    componentDidMount(): void {
        const props = this.props;
        if (props.preloaded && !props.savedFilter) return;
        this.searchItems(Base.filterPropertiesByClass(this.state, MessageListSearchFilter), true, false);
    }

    componentDidUpdate(prevProps: IMessageListProp, prevState: IMessageListState) {
        const props = this.props;
        const state = this.state;
        if (state.waitingPreloadedData) {
            if (!props.list || (props.dataId ?? "") === (prevProps.dataId ?? "")) return;
            this.setState({
                intialized: true,
                waitingPreloadedData: false,
                page: props.list.page,
                hasMore: props.list.hasMore,
                items: props.list.items,
                pageSize: props.list.pageSize,
                readReceiptMessages: this.getReadReceiptMessages(props.list.items),
            });
        } else {
            const prevDataId = prevProps.dataId ? prevProps.dataId : "";
            const dataId = props.dataId ? props.dataId : "";
            const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
            const companyId = props.companyId ? props.companyId : "";
            const prevEditMode = prevProps.editMode ?? false;
            const editMode = props.editMode ?? false;
            const filterStateChange = getMessagePageGeneralFilterChange(prevProps, props);
            const prevSortColumn = prevProps.sortColumn;
            const sortColumn = props.sortColumn;
            const prevSortOrderIsAsc = prevProps.sortOrderIsAsc;
            const sortOrderIsAsc = props.sortOrderIsAsc;
            if (dataId === prevDataId && companyId === prevCompanyId && editMode === prevEditMode &&
                filterStateChange.notChanged &&
                prevSortColumn === sortColumn && prevSortOrderIsAsc === sortOrderIsAsc) return;
            this.searchItems({ ...filterStateChange, pageSize: state.pageSize, page: state.page, filter: state.filter, sortColumn, sortOrderIsAsc }, true, false, false, false);
        }
    }

    changeFilter = (filter: string) => {
        const state = this.state;
        this.searchItems({ ...state, filter }, true, false);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.searchItems({ ...state, page: !nextPage ? 1 : state.page + 1, sortColumn, sortOrderIsAsc }, !nextPage, false);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds
        });
    };

    handleChangeEditMode = (editMode: boolean) => {
        this.props.onChangeEditMode(editMode);
    };

    // #region Edit dialog
    handleShowEditDialog = (recordId: string, forward: boolean) => {
        if (!recordId) return;
        const props = this.props;
        const state = this.state;
        const isNew = recordId === Base.emptyGuid;
        const index = isNew
            ? 0
            : state.items.findIndex(i => i.id === recordId);
        if (index < 0) return;
        this.setState({
            showEditDialog: true,
            editDialogRecordId: recordId,
            editDialogCompanyId: isNew
                ? props.companyId
                : state.items[index].companyId,
            editDialogHasPrevious: index > 0,
            editDialogHasNext: isNew
                ? false
                : index < state.items.length || state.hasMore,
            editDialogForwardMessage: forward
        });
    };

    handleOpenLine = (id: string) => {
        this.handleShowEditDialog(id, false);
    };

    handleAdd = () => {
        this.handleShowEditDialog(Base.emptyGuid, false);
    };

    handleForwardMessage = (id: string) => {
        this.handleShowEditDialog(id, true);
        if (!this.props.editMode) {
            this.props.onChangeEditMode(true);
        }
    };
    
    handleEditDialogOk = () => {
        this.refreshList(false);
        this.props.onLoadPageData();
    };

    handleEditDialogClose = () => {
        this.setState({
            showEditDialog: false,
        }, () => {
            this.refreshList(false);
        });
        this.props.onLoadPageData();
    };

    setEditDialogItemByIndex = (index: number) => {
        const state = this.state;
        if (index < 0) return;
        if (index >= state.items.length) {
            if (!state.hasMore) return;
            this.searchItems({ ...state, page: state.page + 1 }, false, false, true);
        } else {
            this.setState({
                selectedId: state.items[index].id,
                editDialogRecordId: state.items[index].id,
                editDialogCompanyId: state.items[index].companyId,
                editDialogHasPrevious: index > 0,
                editDialogHasNext: index < state.items.length - 1 || state.hasMore
            });
        }
    };
    // #endregion Edit dialog

    // #region Read receipt dialog
    handleMessageReadReceiptDialogClose = (id: string) => {
        if (!id) return;
        const state = this.state;
        const shownReceiptMessageIds = state.shownReceiptMessageIds.slice(0);
        shownReceiptMessageIds.push(id);
        this.setState({
            shownReceiptMessageIds,
            readReceiptMessages: state.readReceiptMessages.filter(i => i.id !== id)
        });
    };
    // #endregion Read receipt dialog

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <>
                <PageTitle title={Translations.CurrentTopics + (props.savedFilter ? " - " + props.savedFilter.title : (props.listFilterIsSet || state.filter ? " - " + Translations.Filtered : ""))}>
                    {props.settingsActions.length > 0 &&
                        <SettingsButton onClick={props.onOpenSettingsDialog}>
                            {Translations.Settings}
                        </SettingsButton>
                    }
                    {props.editMode &&
                        <AddButton variant="contained" onClick={this.handleAdd} sx={{ marginLeft: "1.5rem" }}>
                            {Translations.AddMessage}
                        </AddButton>
                    }
                    {props.canEditMessages &&
                        <EditToggleButtonHeader onClick={() => this.handleChangeEditMode(!props.editMode)} sx={{ marginLeft: "1.5rem" }} selected={props.editMode}>
                        </EditToggleButtonHeader>
                    }
                </PageTitle>
                <MainListContainer>
                    <ActionContainer
                        margin="1rem .5rem 1rem 1rem"
                        leftItems={
                            props.reportTabs
                        }
                        rightItems={
                            <>
                                <ListActionButtons filterIsSet={props.listFilterIsSet} onFilter={props.onOpenFiltersDialog} />
                            </>
                        }
                    />
                    <ListSearchContainer>
                        <ListSearchFilter
                            searchFilter={state.filter}
                            onSearchClick={this.changeFilter}
                        />
                    </ListSearchContainer>
                    <List
                        intialized={state.intialized}
                        isLoading={state.isLoading}
                        hasMore={state.hasMore}
                        disableHoverAndSelectedColors={true}
                        globalLoadingInUse={true}
                        height="calc(100vh - 296px)" //Main List
                        header={null}
                        lines={state.items.map((item) => {
                            return {
                                columns: [
                                    {
                                        size: 12,
                                        column: "modifyDate",
                                        content: <Styled.CardContainer
                                            elevation={3}
                                            selected={state.selectedId === item.id}
                                            sx={{ width: "100%", padding: "16px" }}
                                        >
                                            <Box>
                                                <MessageListHeader
                                                    item={item}
                                                    canEditMessages={props.canEditMessages}
                                                    editMode={props.editMode}
                                                    onForwardMessage={this.handleForwardMessage}
                                                    onOpenMessage={this.handleOpenLine}
                                                />
                                                <MessageContentContainer dangerouslySetInnerHTML={{ __html: item.content }}></MessageContentContainer>
                                                <MessageListAttachments
                                                    item={item}
                                                />
                                            </Box>
                                        </Styled.CardContainer>
                                    },

                                ],
                                id: item.id
                            };
                        })}
                        lineDetails={null}
                        sortColumn={state.sortColumn}
                        sortOrderIsAsc={state.sortOrderIsAsc}
                        selectedId={state.selectedId}
                        checkedIds={state.checkedIds}
                        onSetSelectedIds={this.handleSetSelectedIds}
                        onSearchLines={this.handleSearchLines}
                        onOpenLine={props.editMode ? this.handleOpenLine : null}
                        onItemVisibleToUser={this.handleItemVisibleToUser}
                    />
                    {state.showEditDialog &&
                        <MessageEditDialog
                            messageCompanyId={state.editDialogCompanyId}
                            sessionState={props.sessionState}
                            messageId={state.editDialogRecordId}
                            forwardMessage={state.editDialogForwardMessage}
                            hasPrevious={state.editDialogHasPrevious}
                            hasNext={state.editDialogHasNext}
                            onOk={this.handleEditDialogOk}
                            onClose={this.handleEditDialogClose}
                            onRefreshList={() => this.refreshList(false)}
                            onPrevious={() => List.moveToPrevious(state, this.setEditDialogItemByIndex)}
                            onNext={() => List.moveToNext(state, this.setEditDialogItemByIndex)}
                        />
                    }
                    {!props.editMode && state.readReceiptMessages.length > 0 &&
                        <MessageReadReceiptDialog
                            item={state.readReceiptMessages[0]}
                            onClose={this.handleMessageReadReceiptDialogClose}
                        />
                    }
                </MainListContainer>
            </>
        );
    }
}